import {
  customerFacingClaimReadinessLabel,
  resolveConfiguredCameraLabel,
  resolveConfiguredZoneLabel,
} from '../customerLabels/customerFacingControlLabel';
import { formatSiteDateTime, formatSiteDateTimeRange } from '../siteTime/siteTime';
import { formatCountNoun, formatEpisodeCount } from '../copy/countNoun';
import { COUNT_NOT_AVAILABLE, formatOptionalCount, parseOptionalCount } from './optionalCount';
import {
  type DraftComparisonInput,
  type DraftComparisonPresentation,
  resolveDraftComparisonPresentation,
} from './draftComparisonAuthority';
import { buyerConditionDisplayName } from '../buyerFacing/conditionDisplayNames';

export { COUNT_NOT_AVAILABLE };

export const SAFETY_SCAN_SECTION_ORDER = [
  'problem',
  'evidence',
  'pattern',
  'control',
  'followUp',
  'conclusion',
  'readiness',
] as const;

export type SafetyScanSectionId = (typeof SAFETY_SCAN_SECTION_ORDER)[number];

export const CLAIM_READINESS_CUSTOMER_KEYS = [
  'verification_method',
  'observation_coverage_reliability',
  'field_confirmation_of_zone_controls',
  'matched_window_followup_validation',
] as const;

export const SIGN_SAFETY_SCAN_COPY = {
  button: 'Sign Safety Scan',
  helper:
    'Signing freezes this Safety Scan as a permanent Signed Record revision. New activity after signing is not added to this revision. It will be measured in a later Safety Scan.',
  doesNotCloseCase: 'Signing does not close the Safety Case.',
  doesNotStopMonitoring: 'Signing does not stop monitoring.',
  doesNotProveControl: 'Signing does not prove the Control worked.',
  doesNotMakeComparable: 'Signing does not make the result comparable.',
};

/** Condensed sign-ready helper for the Review → Sign → Record workflow page only. */
export const SIGN_SAFETY_SCAN_WORKFLOW_HELPER =
  'Signing freezes this Safety Scan as a permanent Signed Record. New activity will be measured in a later Safety Scan.';

export function detectionCheckWrapperSubtitle(admitted: boolean, canSign: boolean): string {
  if (!admitted) return 'Downloads work now. Admit evaluation only when you are ready to sign.';
  if (canSign) return 'Ready to sign';
  return 'A frozen detection-performance check is recorded for this Safety Scan.';
}

export const HISTORICAL_FUNNEL_NOT_RETAINED = 'Verification detail not retained in this revision';
export const SIGNED_DISPOSITION_NOT_RETAINED = 'Review disposition not retained';

export type EvidenceFunnelCounts = {
  candidate: number | null;
  reviewed: number | null;
  confirmed: number | null;
  falsePositive: number | null;
  duplicate: number | null;
  needsMoreReview: number | null;
  pending: number | null;
};

export type SafetyScanReadinessItem = {
  key: string;
  label: string;
  complete: boolean;
  blocking: boolean;
};

export type SafetyScanProductPresentation = {
  sectionOrder: SafetyScanSectionId[];
  problem: {
    overline: string;
    conditionLabel: string;
    locationLine: string;
    siteLabel: string;
    areaLabel: string;
    cameraLabel: string;
    windowLabel: string;
    caseLabel: string;
    stateLabel: string;
    technicalIds: string[];
  };
  evidence: {
    funnel: EvidenceFunnelCounts;
    pendingVerifyCount: number | null;
    verifyRequired: boolean;
    verifyHref: string | null;
    representative: Array<{
      timestampLabel: string;
      cameraLabel: string;
      zoneLabel: string;
      dispositionLabel: string;
    }>;
  };
  pattern: {
    summary: string;
    locationLabel: string;
    recurrenceLabel: string;
    windowLabel: string;
    href: string | null;
  };
  control: {
    description: string;
    categoryLabel: string;
    ownerLabel: string;
    statusLabel: string;
    implementedLabel: string;
    caseHref: string | null;
  };
  followUp: {
    windowLabel: string;
    observedAfterLabel: string;
    coverageLabel: string;
    statusLabel: string;
    beforeCount: number | null;
    afterCount: number | null;
    reviewedConfirmedLabel: string;
    pendingReviewLabel: string;
    candidateLabel: string;
    advancedRawSignals: {
      baselineCount: number | null;
      followupCount: number | null;
      heading: string;
      detail: string;
    } | null;
  };
  conclusion: DraftComparisonPresentation;
  readiness: {
    headline: string;
    ready: boolean;
    unavailable: boolean;
    remainingCount: number | null;
    items: SafetyScanReadinessItem[];
    reviewCompleteLabel: string;
    controlRecordedLabel: string;
  };
  history: {
    status: 'WIRED' | 'AVAILABLE_BUT_NOT_WIRED' | 'NONE';
    items: Array<{ label: string; href?: string | null }>;
  };
};

function firstLabel(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

function isRawCustomerToken(value: string): boolean {
  const text = value.trim();
  if (!text) return false;
  return /^(worksafety|cam_walkway_zone_demo|policy_forklift_proximity|workflow_id|matched_window_measurement)$/i.test(text)
    || /^[a-z]+(?:_[a-z0-9]+){2,}$/i.test(text);
}

export function customerScanZoneLabel(raw?: string | null): string {
  const configured = resolveConfiguredZoneLabel(raw);
  if (configured) return configured;
  const text = String(raw || '').trim();
  if (/worksafety/i.test(text)) return 'Walkway Zone';
  return '';
}

export function customerScanCameraLabel(raw?: string | null): string {
  return resolveConfiguredCameraLabel(raw) || '';
}

export function customerFollowUpStatusLabel(raw?: string | null): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (/^insufficient_data$/i.test(text)) return 'Not enough follow-up to compare';
  return text;
}

export function customerScanConditionLabel(raw?: string | null): string {
  const text = String(raw || '').trim();
  if (!text) return 'Safety problem';
  const mapped = buyerConditionDisplayName(text);
  if (mapped !== 'Safety condition') return mapped;
  if (isRawCustomerToken(text)) return 'Safety problem';
  return text;
}

export function formatReviewedObservationLabel(count: number | null): string {
  if (count == null) return `Episodes reviewed · ${COUNT_NOT_AVAILABLE}`;
  return `${formatEpisodeCount(count)} reviewed`;
}

export function formatConfirmedSafetyEventLabel(count: number | null): string {
  if (count == null) return `Confirmed safety events · ${COUNT_NOT_AVAILABLE}`;
  return formatCountNoun(count, 'confirmed safety event');
}

export function formatPendingVerificationLabel(count: number): string {
  const n = Math.max(0, Math.trunc(Number(count) || 0));
  const verb = n === 1 ? 'requires' : 'require';
  return `${formatEpisodeCount(n)} ${verb} review before this Safety Scan can be signed`;
}

export function parseEvidenceFunnelCounts(source?: Record<string, unknown> | null): EvidenceFunnelCounts {
  const progress = source || {};
  return {
    candidate: parseOptionalCount(
      progress.candidate_episode_count ?? progress.candidate_count,
    ),
    reviewed: parseOptionalCount(progress.reviewed_count),
    confirmed: parseOptionalCount(progress.confirmed_count),
    falsePositive: parseOptionalCount(progress.false_positive_count),
    duplicate: parseOptionalCount(progress.duplicate_count),
    needsMoreReview: parseOptionalCount(
      progress.needs_more_review_count ?? progress.unreviewable_count,
    ),
    pending: parseOptionalCount(
      progress.unreviewed ?? progress.pending_review_count ?? progress.pending_count,
    ),
  };
}

export function evidenceFunnelRetained(funnel: EvidenceFunnelCounts): boolean {
  return [
    funnel.candidate,
    funnel.reviewed,
    funnel.confirmed,
    funnel.falsePositive,
    funnel.duplicate,
    funnel.needsMoreReview,
    funnel.pending,
  ].some((value) => value != null);
}

export function formatFunnelCount(value: number | null): string {
  return formatOptionalCount(value, COUNT_NOT_AVAILABLE);
}

export function claimReadinessCustomerLabel(controlId: string, fallbackLabel?: string | null): string {
  return customerFacingClaimReadinessLabel({
    controlId,
    label: fallbackLabel,
  });
}

export function buildSafetyScanReadiness(args: {
  readyToSign: boolean;
  readinessUnknown?: boolean;
  reviewComplete?: boolean;
  reviewReviewed?: number | null;
  reviewCandidate?: number | null;
  pendingVerifyCount?: number | null;
  observationDispositionPending?: boolean;
  controlRecorded?: boolean;
  blockers?: Array<{
    key?: string | null;
    controlId?: string | null;
    label?: string | null;
    complete?: boolean;
    blocking?: boolean;
  }>;
  claimControls?: Array<{
    control_id?: string | null;
    label?: string | null;
    status?: string | null;
    blocking?: boolean | null;
  }>;
}): SafetyScanProductPresentation['readiness'] {
  const pending = parseOptionalCount(args.pendingVerifyCount);
  const pendingBlocking = Boolean(args.observationDispositionPending) || (pending != null && pending > 0);
  const pendingUnknown = pending == null && !args.observationDispositionPending;
  if (args.readinessUnknown) {
    return {
      headline: 'Readiness unavailable',
      ready: false,
      unavailable: true,
      remainingCount: null,
      items: [],
      reviewCompleteLabel: pendingUnknown
        ? 'Review status unavailable'
        : (pendingBlocking
          ? `Review in progress · ${formatPendingVerificationLabel(pending || 0)}`
          : 'Review status unavailable'),
      controlRecordedLabel: args.controlRecorded ? 'Control recorded' : 'Control status unavailable',
    };
  }
  const items: SafetyScanReadinessItem[] = [];
  const seen = new Set<string>();
  for (const control of args.claimControls || []) {
    const key = String(control.control_id || '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const status = String(control.status || '').trim().toLowerCase();
    const complete = status === 'resolved' || status === 'not_applicable' || status === 'waived';
    items.push({
      key,
      label: claimReadinessCustomerLabel(key, control.label),
      complete,
      blocking: control.blocking !== false && !complete,
    });
  }
  for (const blocker of args.blockers || []) {
    const key = String(blocker.controlId || blocker.key || blocker.label || '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    items.push({
      key,
      label: key === 'observation_disposition_pending'
        ? (String(blocker.label || '').trim() || formatPendingVerificationLabel(pending || 0))
        : claimReadinessCustomerLabel(key, blocker.label),
      complete: Boolean(blocker.complete),
      blocking: blocker.blocking !== false,
    });
  }
  if (pendingBlocking && !seen.has('observation_disposition_pending')) {
    items.push({
      key: 'observation_disposition_pending',
      label: pending != null
        ? formatPendingVerificationLabel(pending)
        : 'Required episodes still need a review disposition before Safety Scan sign-off.',
      complete: false,
      blocking: true,
    });
  }
  const remaining = items.filter((item) => !item.complete && item.blocking);
  const ready = Boolean(args.readyToSign) && remaining.length === 0 && !args.readinessUnknown && !pendingBlocking;
  const reviewed = parseOptionalCount(args.reviewReviewed);
  const candidate = parseOptionalCount(args.reviewCandidate);
  let reviewCompleteLabel = 'Review still required';
  if (pendingUnknown) {
    reviewCompleteLabel = 'Review status unavailable';
  } else if (pendingBlocking) {
    reviewCompleteLabel = `Review in progress · ${formatPendingVerificationLabel(pending || 0)}`;
  } else if (pending === 0) {
    reviewCompleteLabel = reviewed != null && candidate != null
      ? `Review complete · ${reviewed} of ${formatEpisodeCount(candidate)} reviewed`
      : 'Review complete';
  }
  let headline = 'Ready to record';
  if (!ready) {
    if (remaining.length <= 0) {
      headline = 'This Safety Scan cannot be finalized yet';
    } else if (remaining.length === 1) {
      headline = '1 item required before this Safety Scan can be finalized';
    } else {
      headline = `${remaining.length} items required before this Safety Scan can be finalized`;
    }
  }
  return {
    headline,
    ready,
    unavailable: false,
    remainingCount: remaining.length,
    items,
    reviewCompleteLabel,
    controlRecordedLabel: args.controlRecorded ? 'Control recorded' : 'Control not recorded',
  };
}

export function buildSafetyScanWindowLabel(
  start?: string | null,
  end?: string | null,
  timeZone?: string | null,
): string {
  if (start && end) return formatSiteDateTimeRange(start, end, timeZone);
  if (start) return formatSiteDateTime(start, timeZone);
  if (end) return formatSiteDateTime(end, timeZone);
  return '';
}

export function buildDraftComparisonFromImpact(args: {
  impact?: {
    measurement_status?: string | null;
    status?: string | null;
    percent_reduction?: number | null;
    baseline_exposure_count?: number | null;
    post_action_exposure_count?: number | null;
    baseline_exposure_rate?: number | null;
    post_action_exposure_rate?: number | null;
    measurement_trust?: {
      effectiveness_claim_allowed?: boolean | null;
      comparability?: Record<string, unknown> | null;
    } | null;
  } | null;
  claimAllowed?: boolean | null;
  comparison?: DraftComparisonInput;
}): DraftComparisonPresentation {
  const impact = args.impact || {};
  const comparability = impact.measurement_trust?.comparability || {};
  const windowsComparable = comparability.windows_comparable === true
    || args.comparison?.windowsComparable === true;
  const denominatorTrusted = String(comparability.denominator_trust || '').toLowerCase() === 'trusted'
    || comparability.denominator_trusted === true
    || args.comparison?.denominatorTrusted === true;
  return resolveDraftComparisonPresentation({
    baselinePresent: args.comparison?.baselinePresent
      ?? (parseOptionalCount(impact.baseline_exposure_count) != null || impact.baseline_exposure_rate != null),
    controlPresent: args.comparison?.controlPresent ?? true,
    recheckPresent: args.comparison?.recheckPresent
      ?? (parseOptionalCount(impact.post_action_exposure_count) != null || impact.post_action_exposure_rate != null),
    windowsComparable: args.comparison?.windowsComparable ?? windowsComparable,
    comparisonClaimable: args.comparison?.comparisonClaimable
      ?? (impact.measurement_trust?.effectiveness_claim_allowed === true && windowsComparable),
    denominatorTrusted: args.comparison?.denominatorTrusted ?? denominatorTrusted,
    coverageGatePassed: args.comparison?.coverageGatePassed ?? true,
    reviewGatePassed: args.comparison?.reviewGatePassed ?? true,
    effectivenessClaimAllowed: args.comparison?.effectivenessClaimAllowed
      ?? args.claimAllowed
      ?? impact.measurement_trust?.effectiveness_claim_allowed === true,
    baselineCount: args.comparison && 'baselineCount' in args.comparison
      ? args.comparison.baselineCount
      : impact.baseline_exposure_count,
    followupCount: args.comparison && 'followupCount' in args.comparison
      ? args.comparison.followupCount
      : impact.post_action_exposure_count,
    baselineRate: args.comparison?.baselineRate ?? impact.baseline_exposure_rate,
    followupRate: args.comparison?.followupRate ?? impact.post_action_exposure_rate,
    percentReduction: args.comparison && 'percentReduction' in args.comparison
      ? args.comparison.percentReduction
      : impact.percent_reduction,
  });
}

export function buildSafetyScanProblemSummary(args: {
  conditionLabel?: string | null;
  siteLabel?: string | null;
  areaLabel?: string | null;
  cameraLabel?: string | null;
  locationLine?: string | null;
  caseLabel?: string | null;
  windowLabel?: string | null;
  signed?: boolean;
  technicalIds?: string[];
}): SafetyScanProductPresentation['problem'] {
  const conditionLabel = customerScanConditionLabel(args.conditionLabel);
  const areaLabel = customerScanZoneLabel(args.areaLabel) || String(args.areaLabel || '').trim();
  const cameraLabel = customerScanCameraLabel(args.cameraLabel);
  const siteLabel = firstLabel(args.siteLabel);
  const locationLine = firstLabel(args.locationLine) || [areaLabel, siteLabel].filter(Boolean).join(' · ');
  return {
    overline: 'Safety Scan',
    conditionLabel,
    locationLine,
    siteLabel,
    areaLabel,
    cameraLabel,
    windowLabel: args.windowLabel || '',
    caseLabel: firstLabel(args.caseLabel, conditionLabel),
    stateLabel: args.signed ? 'Signed' : 'Draft',
    technicalIds: (args.technicalIds || []).filter(Boolean),
  };
}

export function buildSafetyScanProductPresentation(args: {
  conditionLabel?: string | null;
  siteLabel?: string | null;
  areaLabel?: string | null;
  cameraLabel?: string | null;
  locationLine?: string | null;
  caseLabel?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  timeZone?: string | null;
  signed?: boolean;
  technicalIds?: string[];
  funnelSource?: Record<string, unknown> | null;
  pendingVerifyCount?: number | null;
  verifyRequired?: boolean;
  verifyHref?: string | null;
  patternSummary?: string | null;
  recurrenceLabel?: string | null;
  patternHref?: string | null;
  controlDescription?: string | null;
  controlCategory?: string | null;
  controlOwner?: string | null;
  controlStatus?: string | null;
  controlImplemented?: string | null;
  caseHref?: string | null;
  followUpWindowLabel?: string | null;
  followUpCoverage?: string | null;
  followUpStatus?: string | null;
  beforeCount?: number | null;
  afterCount?: number | null;
  rawWorkflowBaselineCount?: number | null;
  rawWorkflowFollowupCount?: number | null;
  comparison?: Parameters<typeof buildDraftComparisonFromImpact>[0];
  readiness?: Parameters<typeof buildSafetyScanReadiness>[0];
}): SafetyScanProductPresentation {
  const windowLabel = buildSafetyScanWindowLabel(args.windowStart, args.windowEnd, args.timeZone);
  const funnel = parseEvidenceFunnelCounts(args.funnelSource);
  const afterCount = parseOptionalCount(args.afterCount);
  const beforeCount = parseOptionalCount(args.beforeCount);
  const rawBaseline = parseOptionalCount(args.rawWorkflowBaselineCount);
  const rawFollowup = parseOptionalCount(args.rawWorkflowFollowupCount);
  const pending = parseOptionalCount(args.pendingVerifyCount) ?? funnel.pending;
  const confirmed = funnel.confirmed;
  const candidate = funnel.candidate;
  const reviewed = funnel.reviewed;
  const problem = buildSafetyScanProblemSummary({
    conditionLabel: args.conditionLabel,
    siteLabel: args.siteLabel,
    areaLabel: args.areaLabel,
    cameraLabel: args.cameraLabel,
    locationLine: args.locationLine,
    caseLabel: args.caseLabel,
    windowLabel,
    signed: args.signed,
    technicalIds: args.technicalIds,
  });
  const scanConfirmedLabel = confirmed == null
    ? COUNT_NOT_AVAILABLE
    : `${formatConfirmedSafetyEventLabel(confirmed)} in this Scan window`;
  const pendingReviewLabel = pending != null && pending > 0
    ? formatPendingVerificationLabel(pending)
    : '';
  const candidateLabel = candidate == null
    ? ''
    : `${formatEpisodeCount(candidate)} in this Scan window`;
  const reviewedConfirmedLabel = reviewed != null && candidate != null
    ? `${reviewed} of ${formatEpisodeCount(candidate)} reviewed`
    : (confirmed == null ? '' : formatConfirmedSafetyEventLabel(confirmed));
  return {
    sectionOrder: [...SAFETY_SCAN_SECTION_ORDER],
    problem,
    evidence: {
      funnel,
      pendingVerifyCount: parseOptionalCount(args.pendingVerifyCount),
      verifyRequired: Boolean(args.verifyRequired) || (parseOptionalCount(args.pendingVerifyCount) != null && Number(parseOptionalCount(args.pendingVerifyCount)) > 0),
      verifyHref: args.verifyHref || null,
      representative: [],
    },
    pattern: {
      summary: args.patternSummary || `Recurring ${problem.conditionLabel.toLowerCase()} exposure`,
      locationLabel: problem.locationLine,
      recurrenceLabel: args.recurrenceLabel || '',
      windowLabel,
      href: args.patternHref || null,
    },
    control: {
      description: args.controlDescription || 'Control not recorded',
      categoryLabel: args.controlCategory || '',
      ownerLabel: args.controlOwner || '',
      statusLabel: args.controlStatus || '',
      implementedLabel: args.controlImplemented || '',
      caseHref: args.caseHref || null,
    },
    followUp: {
      windowLabel: args.followUpWindowLabel || windowLabel,
      observedAfterLabel: scanConfirmedLabel,
      coverageLabel: args.followUpCoverage || '',
      statusLabel: customerFollowUpStatusLabel(args.followUpStatus),
      beforeCount,
      afterCount,
      reviewedConfirmedLabel,
      pendingReviewLabel,
      candidateLabel,
      advancedRawSignals: (rawBaseline != null || rawFollowup != null)
        ? {
            baselineCount: rawBaseline,
            followupCount: rawFollowup,
            heading: 'Raw workflow-linked event signals',
            detail: 'Workflow history; not the reviewed Safety Scan population.',
          }
        : null,
    },
    conclusion: buildDraftComparisonFromImpact({
      ...(args.comparison || {}),
      comparison: {
        ...(args.comparison?.comparison || {}),
        reviewGatePassed: args.comparison?.comparison?.reviewGatePassed
          ?? !(pending != null && pending > 0),
        baselineCount: args.comparison?.comparison?.baselineCount ?? null,
        followupCount: args.comparison?.comparison?.followupCount ?? null,
        percentReduction: args.comparison?.comparison?.percentReduction ?? null,
      },
    }),
    readiness: buildSafetyScanReadiness(args.readiness || { readyToSign: false }),
    history: { status: 'AVAILABLE_BUT_NOT_WIRED', items: [] },
  };
}
